/**
 * DNA Calendário Escolar - Portal das Famílias (DNA Connect)
 * Visualização pública do calendário escolar para famílias
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, CalendarDays, List, Grid3X3, Search, Filter,
  ChevronLeft, ChevronRight, Star, Clock, MapPin, Users,
  PartyPopper, Umbrella, FileText, Bell, Download
} from 'lucide-react';

const API_URL = window.location.origin || '';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CATEGORIA_CONFIG = {
  eventos_escolares: { 
    label: 'Eventos Escolares', 
    bg: 'bg-purple-500', 
    bgLight: 'bg-purple-100',
    textLight: 'text-purple-700',
    border: 'border-purple-500',
    icon: PartyPopper 
  },
  feriados: { 
    label: 'Feriados', 
    bg: 'bg-green-500', 
    bgLight: 'bg-green-100',
    textLight: 'text-green-700',
    border: 'border-green-500',
    icon: Calendar 
  },
  atividades_suspensas: { 
    label: 'Atividades Suspensas', 
    bg: 'bg-orange-500', 
    bgLight: 'bg-orange-100',
    textLight: 'text-orange-700',
    border: 'border-orange-500',
    icon: Umbrella 
  },
  avaliacoes: { 
    label: 'Avaliações', 
    bg: 'bg-blue-500', 
    bgLight: 'bg-blue-100',
    textLight: 'text-blue-700',
    border: 'border-blue-500',
    icon: FileText 
  }
};

const PUBLICO_LABELS = {
  educacao_infantil: 'Ed. Infantil',
  fundamental_i: 'Fund. I',
  fundamental_ii: 'Fund. II',
  ensino_medio: 'Ensino Médio',
  familias: 'Famílias',
  geral: 'Geral'
};

export default function CalendarioEscolarPublico() {
  // Estados principais
  const [viewMode, setViewMode] = useState('mes');
  const [eventos, setEventos] = useState([]);
  const [proximosEventos, setProximosEventos] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Estados de navegação
  const [anoAtual, setAnoAtual] = useState(2026);
  const [mesAtual, setMesAtual] = useState(new Date().getMonth() + 1);
  
  // Estados de filtro
  const [filtroCategoria, setFiltroCategoria] = useState('all');
  const [filtroBusca, setFiltroBusca] = useState('');
  
  // Modal de detalhes
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [showDetalhes, setShowDetalhes] = useState(false);
  
  // Estados de notificações
  const [notificacoesAtivas, setNotificacoesAtivas] = useState(false);
  const [notificacoesPendentes, setNotificacoesPendentes] = useState([]);
  const [showNotificacaoModal, setShowNotificacaoModal] = useState(false);
  
  // Verificar suporte a notificações e permissão
  const verificarSuporteNotificacoes = useCallback(() => {
    if ('Notification' in window) {
      return Notification.permission;
    }
    return 'unsupported';
  }, []);
  
  // Solicitar permissão de notificação
  const solicitarPermissaoNotificacao = async () => {
    if (!('Notification' in window)) {
      toast.error('Seu navegador não suporta notificações');
      return;
    }
    
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificacoesAtivas(true);
        localStorage.setItem('calendario_notificacoes', 'ativo');
        toast.success('Notificações ativadas! Você receberá lembretes dos eventos.');
        verificarEventosProximos();
      } else {
        toast.info('Permissão de notificação negada');
      }
    } catch (error) {
      console.error('Erro ao solicitar permissão:', error);
    }
  };
  
  // Verificar eventos que acontecerão em 2 dias
  const verificarEventosProximos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendario/eventos/publico/notificacoes?dias_antecedencia=2`);
      const data = await res.json();
      
      if (data.notificacoes && data.notificacoes.length > 0) {
        // Verificar quais eventos ainda não foram notificados
        const notificacoesNaoEnviadas = [];
        
        for (const notif of data.notificacoes) {
          const jaNotificado = localStorage.getItem(`notif_${notif.id}`);
          if (!jaNotificado) {
            notificacoesNaoEnviadas.push(notif);
          }
        }
        
        setNotificacoesPendentes(notificacoesNaoEnviadas);
        
        // Enviar notificações push para eventos não notificados
        if (notificacoesAtivas && Notification.permission === 'granted') {
          notificacoesNaoEnviadas.forEach(notif => {
            enviarNotificacaoPush(notif);
          });
        }
      }
    } catch (error) {
      console.error('Erro ao verificar eventos próximos:', error);
    }
  }, [notificacoesAtivas]);
  
  // Enviar notificação push
  const enviarNotificacaoPush = (evento) => {
    if (Notification.permission === 'granted') {
      const options = {
        body: evento.push_body || `O evento "${evento.titulo}" acontecerá em breve!`,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: evento.id,
        requireInteraction: true,
        vibrate: [200, 100, 200]
      };
      
      try {
        const notification = new Notification(evento.push_title || evento.titulo, options);
        
        notification.onclick = () => {
          window.focus();
          setEventoSelecionado(evento);
          setShowDetalhes(true);
          notification.close();
        };
        
        // Marcar como notificado no localStorage
        localStorage.setItem(`notif_${evento.id}`, Date.now().toString());
        
        // Registrar no backend
        fetch(`${API_URL}/api/calendario/eventos/publico/registrar-notificacao?evento_id=${evento.id}`, {
          method: 'POST'
        }).catch(console.error);
        
      } catch (error) {
        console.error('Erro ao enviar notificação:', error);
      }
    }
  };
  
  // Verificar se notificações estão ativas no localStorage
  useEffect(() => {
    const notifAtivas = localStorage.getItem('calendario_notificacoes');
    if (notifAtivas === 'ativo' && Notification.permission === 'granted') {
      setNotificacoesAtivas(true);
    }
  }, []);
  
  // Verificar eventos próximos ao carregar e periodicamente
  useEffect(() => {
    if (notificacoesAtivas) {
      verificarEventosProximos();
      
      // Verificar a cada 6 horas
      const interval = setInterval(verificarEventosProximos, 6 * 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [notificacoesAtivas, verificarEventosProximos]);
  
  // Carregar eventos públicos
  const carregarEventos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('ano', anoAtual);
      if (viewMode === 'mes') {
        params.append('mes', mesAtual);
      }
      if (filtroCategoria !== 'all') {
        params.append('categoria', filtroCategoria);
      }
      if (filtroBusca) {
        params.append('busca', filtroBusca);
      }
      
      const res = await fetch(`${API_URL}/api/calendario/eventos/publico?${params.toString()}`);
      const data = await res.json();
      setEventos(data.eventos || []);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  }, [anoAtual, mesAtual, viewMode, filtroCategoria, filtroBusca]);
  
  const carregarProximosEventos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendario/eventos/publico/proximos?limite=5`);
      const data = await res.json();
      setProximosEventos(data.proximos || []);
    } catch (error) {
      console.error('Erro ao carregar próximos eventos:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarEventos();
  }, [carregarEventos]);
  
  useEffect(() => {
    carregarProximosEventos();
  }, [carregarProximosEventos]);
  
  // Navegação de mês
  const mesAnterior = () => {
    if (mesAtual === 1) {
      setMesAtual(12);
      setAnoAtual(anoAtual - 1);
    } else {
      setMesAtual(mesAtual - 1);
    }
  };
  
  const proximoMes = () => {
    if (mesAtual === 12) {
      setMesAtual(1);
      setAnoAtual(anoAtual + 1);
    } else {
      setMesAtual(mesAtual + 1);
    }
  };
  
  // Gerar dias do mês para o calendário
  const gerarDiasMes = () => {
    const primeiroDia = new Date(anoAtual, mesAtual - 1, 1);
    const ultimoDia = new Date(anoAtual, mesAtual, 0);
    const diasNoMes = ultimoDia.getDate();
    const diaInicio = primeiroDia.getDay();
    
    const dias = [];
    
    for (let i = 0; i < diaInicio; i++) {
      dias.push({ dia: null, eventos: [] });
    }
    
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const eventosNoDia = eventos.filter(e => e.dia === dia);
      dias.push({ dia, eventos: eventosNoDia });
    }
    
    return dias;
  };
  
  // Formatar data — timezone-safe (sem deslocar fuso)
  const MESES_PT = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
  const formatarData = (dataStr) => {
    if (!dataStr) return '';
    const m = String(dataStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]} de ${MESES_PT[parseInt(m[2], 10) - 1]}` : dataStr;
  };

  const formatarDataCurta = (dataStr) => {
    if (!dataStr) return '';
    const m = String(dataStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}` : dataStr;
  };
  
  // Componente Card de Evento
  const EventoCard = ({ evento, compact = false }) => {
    const config = CATEGORIA_CONFIG[evento.categoria] || CATEGORIA_CONFIG.eventos_escolares;
    const Icon = config.icon;
    
    if (compact) {
      return (
        <div 
          className={`text-xs p-1 rounded truncate cursor-pointer ${config.bg} text-white`}
          onClick={() => {
            setEventoSelecionado(evento);
            setShowDetalhes(true);
          }}
          title={evento.titulo}
        >
          {evento.destaque && <Star className="w-2 h-2 inline mr-1 fill-yellow-300" />}
          {evento.titulo}
        </div>
      );
    }
    
    return (
      <Card 
        className={`hover:shadow-lg transition-all cursor-pointer border-l-4 ${config.border} bg-white`}
        onClick={() => {
          setEventoSelecionado(evento);
          setShowDetalhes(true);
        }}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`p-3 rounded-xl ${config.bgLight} shrink-0`}>
              <Icon className={`w-6 h-6 ${config.textLight}`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {evento.destaque && (
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 shrink-0" />
                )}
                <h4 className="font-semibold text-gray-800 truncate">{evento.titulo}</h4>
              </div>
              
              <p className="text-sm text-gray-500 mb-2">
                {formatarData(evento.data_inicio)}
                {evento.data_fim && evento.data_fim !== evento.data_inicio && (
                  <> a {formatarData(evento.data_fim)}</>
                )}
              </p>
              
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`${config.bg} text-white text-xs`}>
                  {config.label}
                </Badge>
                {evento.publico_alvo?.filter(p => p !== 'geral').slice(0, 2).map(p => (
                  <Badge key={p} variant="outline" className="text-xs">
                    {PUBLICO_LABELS[p] || p}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-green-50">
      {/* Header Premium */}
      <div className="bg-gradient-to-r from-purple-600 to-green-600 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-10 h-10" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  Calendário Escolar 2026
                </h1>
                <p className="text-purple-100 text-sm md:text-base">
                  NOVO DNA Instituto de Aprendizagem
                </p>
              </div>
            </div>
            
            {/* Botão de Notificações */}
            <Button
              variant={notificacoesAtivas ? "secondary" : "outline"}
              size="sm"
              onClick={notificacoesAtivas ? () => setShowNotificacaoModal(true) : solicitarPermissaoNotificacao}
              className={`flex items-center gap-2 ${notificacoesAtivas ? 'bg-white text-purple-700' : 'bg-white/20 text-white border-white/40 hover:bg-white/30'}`}
              data-testid="btn-notificacoes"
            >
              <Bell className={`w-4 h-4 ${notificacoesAtivas ? 'fill-purple-500' : ''}`} />
              <span className="hidden sm:inline">
                {notificacoesAtivas ? 'Lembretes Ativos' : 'Ativar Lembretes'}
              </span>
              {notificacoesPendentes.length > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {notificacoesPendentes.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
      
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Próximos Eventos - Widget Destaque */}
        {proximosEventos.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Próximos Eventos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {proximosEventos.map(evento => (
                  <div 
                    key={evento.id}
                    className="bg-white/20 backdrop-blur rounded-xl p-3 min-w-48 cursor-pointer hover:bg-white/30 transition-colors"
                    onClick={() => {
                      setEventoSelecionado(evento);
                      setShowDetalhes(true);
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {evento.destaque && <Star className="w-3 h-3 fill-yellow-300" />}
                      <span className="text-xs font-medium">{formatarDataCurta(evento.data_inicio)}</span>
                    </div>
                    <p className="font-medium text-sm truncate">{evento.titulo}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Filtros e Controles */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              {/* Busca */}
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Buscar eventos..."
                  value={filtroBusca}
                  onChange={(e) => setFiltroBusca(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filtro categoria */}
              <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                <SelectTrigger className="w-full md:w-52">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filtrar categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas categorias</SelectItem>
                  {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${config.bg}`} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Modo de visualização */}
              <div className="flex items-center gap-1 border rounded-lg p-1">
                <Button 
                  variant={viewMode === 'mes' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('mes')}
                  className={viewMode === 'mes' ? 'bg-purple-600' : ''}
                >
                  <CalendarDays className="w-4 h-4" />
                </Button>
                <Button 
                  variant={viewMode === 'lista' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('lista')}
                  className={viewMode === 'lista' ? 'bg-purple-600' : ''}
                >
                  <List className="w-4 h-4" />
                </Button>
                <Button 
                  variant={viewMode === 'cards' ? 'default' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className={viewMode === 'cards' ? 'bg-purple-600' : ''}
                >
                  <Grid3X3 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Navegação de mês */}
            {viewMode === 'mes' && (
              <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t">
                <Button variant="outline" size="icon" onClick={mesAnterior}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <h2 className="text-xl font-bold text-purple-700 min-w-40 text-center">
                  {MESES[mesAtual - 1]} {anoAtual}
                </h2>
                <Button variant="outline" size="icon" onClick={proximoMes}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Conteúdo Principal */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Visualização Mês (Calendário) */}
            {viewMode === 'mes' && (
              <Card className="overflow-hidden">
                <CardContent className="p-2 md:p-4">
                  {/* Cabeçalho dos dias */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DIAS_SEMANA.map(dia => (
                      <div key={dia} className="text-center font-medium text-gray-500 text-xs md:text-sm py-2">
                        {dia}
                      </div>
                    ))}
                  </div>
                  
                  {/* Grid do calendário */}
                  <div className="grid grid-cols-7 gap-1">
                    {gerarDiasMes().map((item, index) => (
                      <div 
                        key={index}
                        className={`min-h-16 md:min-h-24 border rounded-lg p-1 ${
                          item.dia ? 'bg-white hover:bg-purple-50 transition-colors' : 'bg-gray-50'
                        }`}
                      >
                        {item.dia && (
                          <>
                            <div className={`text-right text-xs md:text-sm font-medium mb-1 ${
                              item.eventos.length > 0 ? 'text-purple-700' : 'text-gray-500'
                            }`}>
                              {item.dia}
                            </div>
                            <div className="space-y-1">
                              {item.eventos.slice(0, 2).map(evento => (
                                <EventoCard key={evento.id} evento={evento} compact />
                              ))}
                              {item.eventos.length > 2 && (
                                <p className="text-xs text-purple-600 text-center font-medium">
                                  +{item.eventos.length - 2}
                                </p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Visualização Lista */}
            {viewMode === 'lista' && (
              <Card>
                <CardContent className="p-4">
                  {eventos.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg">Nenhum evento encontrado</p>
                      <p className="text-sm mt-1">Tente ajustar os filtros</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {eventos.map(evento => (
                        <EventoCard key={evento.id} evento={evento} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Visualização Cards */}
            {viewMode === 'cards' && (
              <>
                {eventos.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center text-gray-500">
                      <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg">Nenhum evento encontrado</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {eventos.map(evento => (
                      <EventoCard key={evento.id} evento={evento} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}
        
        {/* Legenda */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-gray-600 mb-3">Legenda das Categorias:</p>
            <div className="flex flex-wrap gap-4">
              {Object.entries(CATEGORIA_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded ${config.bg}`} />
                    <Icon className={`w-4 h-4 ${config.textLight}`} />
                    <span className="text-sm text-gray-600">{config.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center mt-8 pb-6">
          <p className="text-sm text-gray-500">
            NOVO DNA Instituto de Aprendizagem • Taboão da Serra - SP
          </p>
          <p className="text-xs text-gray-400 mt-1">
            "Muitas mentes, uma missão!"
          </p>
        </div>
      </div>
      
      {/* Modal Detalhes do Evento */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-md">
          {eventoSelecionado && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  {eventoSelecionado.destaque && (
                    <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  )}
                  {eventoSelecionado.titulo}
                </DialogTitle>
                <DialogDescription className="text-base">
                  {formatarData(eventoSelecionado.data_inicio)}
                  {eventoSelecionado.data_fim && eventoSelecionado.data_fim !== eventoSelecionado.data_inicio && (
                    <> a {formatarData(eventoSelecionado.data_fim)}</>
                  )}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <Badge 
                  className={`${CATEGORIA_CONFIG[eventoSelecionado.categoria]?.bg || 'bg-gray-500'} text-white text-sm px-3 py-1`}
                >
                  {CATEGORIA_CONFIG[eventoSelecionado.categoria]?.label || eventoSelecionado.categoria}
                </Badge>
                
                {eventoSelecionado.descricao && (
                  <p className="text-gray-600 leading-relaxed">{eventoSelecionado.descricao}</p>
                )}
                
                <div className="space-y-2 text-gray-600">
                  {eventoSelecionado.local && (
                    <div className="flex items-center gap-3">
                      <MapPin className="w-5 h-5 text-purple-500" />
                      <span>{eventoSelecionado.local}</span>
                    </div>
                  )}
                  
                  {eventoSelecionado.horario && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-purple-500" />
                      <span>{eventoSelecionado.horario}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-purple-500" />
                    <span>
                      {eventoSelecionado.publico_alvo?.map(p => 
                        PUBLICO_LABELS[p] || p
                      ).join(', ')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDetalhes(false)}
                  className="w-full"
                >
                  Fechar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Notificações */}
      <Dialog open={showNotificacaoModal} onOpenChange={setShowNotificacaoModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Bell className="w-6 h-6 text-purple-600 fill-purple-200" />
              Lembretes de Eventos
            </DialogTitle>
            <DialogDescription>
              Você receberá notificações 2 dias antes dos eventos importantes.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Status das notificações */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-full">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-green-800">Lembretes Ativos</p>
                  <p className="text-sm text-green-600">Você será notificado sobre os próximos eventos</p>
                </div>
              </div>
            </div>
            
            {/* Próximos lembretes */}
            {notificacoesPendentes.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-3">Eventos em breve:</h4>
                <div className="space-y-2">
                  {notificacoesPendentes.map(notif => (
                    <div 
                      key={notif.id}
                      className="p-3 bg-purple-50 rounded-lg border border-purple-200 cursor-pointer hover:bg-purple-100 transition-colors"
                      onClick={() => {
                        setEventoSelecionado(notif);
                        setShowNotificacaoModal(false);
                        setShowDetalhes(true);
                      }}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium text-purple-800">{notif.titulo}</span>
                      </div>
                      <p className="text-sm text-purple-600">
                        {formatarData(notif.data_inicio)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {notificacoesPendentes.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhum evento próximo para notificação.</p>
                <p className="text-sm mt-1">
                  Você será avisado 2 dias antes de cada evento.
                </p>
              </div>
            )}
            
            {/* Opção de desativar */}
            <div className="pt-4 border-t">
              <Button 
                variant="outline"
                size="sm"
                className="w-full text-red-600 border-red-200 hover:bg-red-50"
                onClick={() => {
                  setNotificacoesAtivas(false);
                  localStorage.removeItem('calendario_notificacoes');
                  setShowNotificacaoModal(false);
                  toast.info('Lembretes desativados');
                }}
              >
                Desativar Lembretes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
